import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
// import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  ShoppingCart,
  User,
  Menu,
  Heart,
  MapPin,
  Phone,
  Clock,
  Home,
  Store,
  Info,
  FileText,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "../../assets/images/logo/logo.png";
import { useAuth } from "@/context/Auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { loadCart, selectCartCount } from "@/store/slices/cartSlice";

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [isScrolled, setIsScrolled] = useState(false);
  const mobileSearchInputRef = useRef(null);
  
  // Get cart count from Redux
  const cartCount = useAppSelector(selectCartCount);

  // Load cart on mount and when user changes
  useEffect(() => {
    dispatch(loadCart(user));
  }, [dispatch, user]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
  
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Prevent auto-focus on mobile search input when sidebar opens
  useEffect(() => {
    if (isMobileMenuOpen && mobileSearchInputRef.current) {
      const input = mobileSearchInputRef.current;
      
      // Blur the input immediately when sidebar opens
      const blurInput = () => {
        if (document.activeElement === input) {
          input.blur();
        }
      };
      
      // Blur immediately and set up a listener to catch any delayed focus
      blurInput();
      const timeoutId1 = setTimeout(blurInput, 50);
      const timeoutId2 = setTimeout(blurInput, 150);
      const timeoutId3 = setTimeout(blurInput, 300);
      
      // Also listen for focus events and blur immediately
      const handleFocus = () => {
        setTimeout(() => {
          if (document.activeElement === input) {
            input.blur();
          }
        }, 0);
      };
      
      input.addEventListener('focus', handleFocus);
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        input.removeEventListener('focus', handleFocus);
      };
    }
  }, [isMobileMenuOpen]);

  const navigationItems = [
    { name: "Home", to: "/", icon: Home },
    { name: "Shop", to: "shop", icon: Store },
    { name: "About us", to: "/about-us", icon: Info },
    // { name: "Our Story", to: "#" },
    { name: "Blog", to: "/blogs", icon: FileText },
    { name: "Contact Us", to: "/contact-us", icon: Mail },
  ];

  const accountItems = [
    { name: "My Account", to: "/dashboard", icon: User },
    { name: "Order History", to: "/dashboard/orders", icon: Search },
    // { name: "Wishlist", to: "/wishlist", icon: Heart },
    { name: "Track Order", to: "/track-order", icon: MapPin },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top bar */}
        {/* <div className="hidden md:flex items-center justify-between py-2 text-sm border-b border-gray-100">
          <div className="text-primary">
            Free shipping on orders over $50 | 30-day money-back guarantee
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/track-order"
              className="text-primary hover:text-secondary transition-colors"
            >
              Track Order
            </Link>
            <Link
              to="#"
              className="text-primary hover:text-secondary transition-colors"
            >
              Help
            </Link>
          </div>
        </div> */}

        {/* Main header */}
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src={Logo}
              style={{ width: "120px", height: "40px", align: "center" }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                className="text-primary hover:text-secondary transition-colors font-medium relative"
              >
                {item.name}
                {item.badge && (
                  <Badge className="absolute -top-2 -right-6 bg-secondary text-primary text-xs px-1 py-0">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            ))}
          </nav>

          {/* Search and actions */}
          <div className="flex items-center gap-4">
            {/* Desktop Search */}
            <div className="hidden md:flex items-center relative">
              <Input
                placeholder="Search products..."
                className="w-40 sm:w-56 md:w-64 pr-10 border-gray-200 focus:border-secondary"
              />
              <Search className="w-4 h-4 absolute right-3 text-gray-400" />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {user ? (
                <div className="hidden md:flex items-center gap-2">
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-medium text-primary">
                      {user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email}
                    </span>
                    <Badge variant="outline" className="text-xs mt-0.5">
                      {user.role || "Customer"}
                    </Badge>
                  </div>
                  <Link to="/dashboard">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-primary"
                    >
                      <User className="w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <Link to="/auth/login">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="hidden md:flex text-primary"
                  >
                    <User className="w-5 h-5" />
                  </Button>
                </Link>
              )}
              <Link to="/cart">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-primary relative"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-secondary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* Mobile Menu Trigger */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="block text-primary md:hidden"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:w-80 p-0 overflow-y-auto">
                  <div className="flex flex-col h-full bg-white">
                    {/* Mobile Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100">
                      <Link
                        to="/"
                        className="flex items-center gap-2"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <img
                          src={Logo}
                          style={{
                            width: "120px",
                            height: "40px",
                            align: "center",
                          }}
                        />
                      </Link>
                    </div>

                    {/* Mobile Search */}
                    <div className="p-6 border-b border-gray-100">
                      <div className="relative">
                        <Input
                          ref={mobileSearchInputRef}
                          placeholder="Search products..."
                          className="pr-10 border-gray-200 focus:border-secondary"
                          autoFocus={false}
                        />
                        <Search className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      </div>
                    </div>

                    {/* Mobile Navigation */}
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-sm font-semibold text-primary mb-3">Menu</h3>
                      <nav className="space-y-2">
                        {navigationItems.map((item) => (
                          <Link
                            key={item.name}
                            to={item.to}
                            className="flex items-center gap-3 p-3 rounded-lg text-primary hover:bg-secondary/10 transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.icon && (
                              <item.icon className="w-5 h-5 text-secondary" />
                            )}
                            <span className="font-medium">{item.name}</span>
                            {item.badge && (
                              <Badge className="ml-auto bg-secondary text-primary text-xs px-1 py-0">
                                {item.badge}
                              </Badge>
                            )}
                          </Link>
                        ))}
                      </nav>
                    </div>

                    {/* Account Section */}
                    <div className="p-6 border-b border-gray-100">
                      {user ? (
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-secondary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-primary">
                              {user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email}
                            </p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {user.role || "Customer"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-secondary" />
                          </div>
                          <div>
                            <p className="font-semibold text-primary">
                              Welcome back!
                            </p>
                            <Link
                              to="/auth/login"
                              className="text-sm text-secondary hover:text-secondary/80"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              Sign in to your account
                            </Link>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        {accountItems.map((item) => (
                          <Link
                            key={item.name}
                            to={item.to}
                            className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-secondary/10 transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <item.icon className="w-4 h-4 text-secondary" />
                            <span className="text-sm font-medium text-primary">
                              {item.name}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-100 bg-gray-50">
                      {/* <div className="space-y-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-secondary" />
                          <span>1-800-GLOW-SKIN</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4 text-secondary" />
                          <span>Mon-Fri 9AM-6PM EST</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 text-secondary" />
                          <span>Free shipping over $50</span>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
